import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ChevronDown, History, Loader2, Mail, Send } from "lucide-react";
import { Link } from "wouter";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

/**
 * Admin diagnostics surface for the seed-failure email notifier (task #182).
 *
 * Task #157 added a one-time admin email when preset templates fail to seed
 * for a workspace. The dedup row in `seed_automation_failure_notifications`
 * is the system's only memory of who got paged and when, but until task #200
 * it only stored the *most recent* sender — when the workspace's Gmail
 * connector account changed between sends, the audit trail of "which inbox
 * sent the April 12 notice" was lost on the next send.
 *
 * Task #200 adds a per-fan-out append-only log (`seed_automation_failure_notification_log`)
 * and a matching history endpoint. This component now renders both:
 *   - The "Last notice sent" line still surfaces the most recent dedup row
 *     for an at-a-glance answer.
 *   - The new "Recent sends" list shows the chronological audit trail so
 *     admins can see which sender each individual notice came from.
 *
 * Hidden when `enabled=false` so non-admin pages can mount it without
 * spamming the admin endpoints (mirrors the SeedFailureBanner gate).
 */
// Reasons the recorded sender on a status row can be missing (task
// #227). Surfaced by the backend so the UI doesn't have to recompute
// admin/connector availability on the client. `null` whenever
// `senderEmail` is recorded — the UI then renders the "Sent as
// <email>" line instead of the missing-sender hint.
type SenderMissingReason = "no-admins" | "no-connector" | "legacy-row";

type NotificationStatus = {
  workspaceId: string;
  lastNotifiedAt: string | null;
  notifiedSeedKeys: string[];
  // Sender attribution (task #192). `senderEmail` is the Gmail
  // connector address the notice was actually delivered from — admins
  // need this to know which inbox to check when a test notice doesn't
  // show up. Null on legacy rows that pre-date the column.
  senderUserId: string | null;
  senderEmail: string | null;
  // Why the recorded sender is missing (task #227). Drives the
  // actionable hint we render in place of the generic "Sender not
  // recorded" string when `senderEmail` is null.
  senderMissingReason: SenderMissingReason | null;
};

// One row from the per-fan-out audit log (task #200). Mirrors the JSON
// shape returned by `GET /api/admin/seed-automations/notification-history`.
type NotificationHistoryEntry = {
  id: string;
  sentAt: string;
  signature: string;
  notifiedSeedKeys: string[];
  senderUserId: string | null;
  senderEmail: string | null;
  recipientCount: number;
};

type NotificationHistoryResponse = {
  workspaceId: string;
  limit: number;
  entries: NotificationHistoryEntry[];
  // Cursor for the next page (task #220). When the server returns a
  // full page it surfaces the oldest row's sentAt here so the
  // "Load older" affordance can ask for everything strictly older.
  // Null when we've reached the tail of the log.
  nextCursor: string | null;
};

type TestSendResponse = NotificationStatus & {
  sent: boolean;
  reason: string | null;
  recipientCount: number;
  isTestSentinel: boolean;
};

const STATUS_KEY = ["/api/admin/seed-automations/notification-status"] as const;
const HISTORY_KEY = ["/api/admin/seed-automations/notification-history"] as const;

interface SeedFailureNotificationHistoryProps {
  enabled: boolean;
  className?: string;
}

/**
 * Renders the contextual hint that replaces the generic "Sender not
 * recorded" string when the status row's `senderEmail` is null
 * (task #227). The reason comes from the backend so this component
 * just decides the copy + the actionable link to surface:
 *
 *   - `no-admins`     → invite an admin via /admin?section=users
 *   - `no-connector`  → connect Gmail via /admin?section=integrations
 *   - `legacy-row`    → informational only; the next failure or boot
 *                       backfill pass repopulates the row, no admin
 *                       action required
 *
 * Each branch carries a stable `data-testid` so the component test
 * suite can assert which hint actually rendered without leaning on
 * brittle text matching. The wouter `Link` keeps workspace-prefixed
 * routing intact (the ai-error helper uses the same `/admin?section=`
 * convention against the workspace-aware router base).
 */
function SenderMissingHint({ reason }: { reason: SenderMissingReason }) {
  if (reason === "no-admins") {
    return (
      <span
        className="text-xs italic text-muted-foreground"
        data-testid="text-seed-notification-sender-missing-no-admins"
      >
        Sender not recorded — this workspace has no admin to attribute
        the notice to.{" "}
        <Link
          href="/admin?section=users"
          className="not-italic underline hover:no-underline"
          data-testid="link-seed-notification-sender-invite-admin"
        >
          Invite an admin
        </Link>{" "}
        so the next notice can be recorded.
      </span>
    );
  }
  if (reason === "no-connector") {
    return (
      <span
        className="text-xs italic text-muted-foreground"
        data-testid="text-seed-notification-sender-missing-no-connector"
      >
        Sender not recorded — Gmail isn't connected for this workspace.{" "}
        <Link
          href="/admin?section=integrations"
          className="not-italic underline hover:no-underline"
          data-testid="link-seed-notification-sender-connect-gmail"
        >
          Connect Gmail
        </Link>{" "}
        so future notices can record a sender.
      </span>
    );
  }
  // `legacy-row` — admins + connector are both available now, the row
  // just pre-dates the attribution columns and hasn't been re-paged
  // yet. No admin action required, so we render an explanation
  // without a call-to-action link.
  return (
    <span
      className="text-xs italic text-muted-foreground"
      data-testid="text-seed-notification-sender-missing-legacy"
    >
      Sender not recorded — this notice was sent before sender
      attribution was tracked. The next failure (or the next boot-time
      backfill) will repopulate it.
    </span>
  );
}

export function SeedFailureNotificationHistory({
  enabled,
  className,
}: SeedFailureNotificationHistoryProps) {
  const { toast } = useToast();

  const statusQuery = useQuery<NotificationStatus>({
    queryKey: STATUS_KEY,
    enabled,
  });

  // Per-fan-out history (task #200). Cursor-paged via useInfiniteQuery
  // (task #220) so admins investigating an older incident can scroll
  // past the most recent page (server-side cap is 100 per page)
  // without having to query the DB by hand. The default queryFn won't
  // do — it just joins the queryKey into a URL — so we provide one
  // here that forwards the cursor as `?before=<iso8601>`.
  const historyQuery = useInfiniteQuery<NotificationHistoryResponse>({
    queryKey: HISTORY_KEY,
    enabled,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? null,
    queryFn: async ({ pageParam }) => {
      const url = pageParam
        ? `/api/admin/seed-automations/notification-history?before=${encodeURIComponent(
            String(pageParam),
          )}`
        : "/api/admin/seed-automations/notification-history";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        throw new Error(`${res.status}: ${await res.text()}`);
      }
      return res.json();
    },
  });

  const testSendMutation = useMutation({
    mutationFn: async () => {
      return apiRequest(
        "POST",
        "/api/admin/seed-automations/notification-test-send",
      );
    },
    onSuccess: async (response: any) => {
      const data: TestSendResponse = await response.json();
      // Push the new status into the cache so the "Last notice sent" row
      // updates immediately without waiting for a refetch round-trip.
      queryClient.setQueryData<NotificationStatus>(STATUS_KEY, {
        workspaceId: data.workspaceId,
        lastNotifiedAt: data.lastNotifiedAt,
        notifiedSeedKeys: data.notifiedSeedKeys,
        senderUserId: data.senderUserId,
        senderEmail: data.senderEmail,
        // Mirror the missing-sender reason from the test-send payload
        // so the cached status stays in sync with what a fresh GET
        // would return (task #227). Defaults to null when the field
        // is absent so older mocked responses keep working.
        senderMissingReason: data.senderMissingReason ?? null,
      });
      // The fan-out log gained a new row server-side — invalidate the
      // history query so the chronological list reflects the freshly
      // recorded send. We invalidate (rather than synthesizing the row
      // client-side) because the log row's id and sentAt are server-
      // generated and we want the canonical values.
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
      if (data.sent) {
        // Surface the actual sender (task #192) so admins know which
        // inbox to check when the test notice doesn't show up in their
        // own — it likely went out from the workspace Gmail connector
        // address rather than their personal Google account.
        const baseDescription = data.isTestSentinel
          ? `Sent a test notice to ${data.recipientCount} admin(s). No real seed failures detected — used a placeholder seed key.`
          : `Sent a fresh notice to ${data.recipientCount} admin(s) for the current failing preset templates.`;
        toast({
          title: "Test notice sent",
          description: data.senderEmail
            ? `${baseDescription} Sent as ${data.senderEmail}.`
            : baseDescription,
        });
      } else {
        const reasonLabel: Record<string, string> = {
          "no-failures": "No failed seed keys to send for.",
          "no-admins": "No admin recipients found for this workspace.",
          duplicate: "A matching notice was already sent (dedup hit).",
          "all-sends-failed": "Every email send failed — check Gmail connector.",
          "db-error": "Database error reading the dedup row.",
        };
        toast({
          title: "Test notice not sent",
          description: reasonLabel[data.reason ?? ""] ?? "Notice could not be sent.",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Test notice failed",
        description: error?.message || "Could not send the test notice.",
        variant: "destructive",
      });
    },
  });

  if (!enabled) return null;

  const status = statusQuery.data;
  const lastNotifiedAt = status?.lastNotifiedAt
    ? new Date(status.lastNotifiedAt)
    : null;
  const notifiedSeedKeys = status?.notifiedSeedKeys ?? [];
  const senderEmail = status?.senderEmail ?? null;
  // Default to "legacy-row" when the backend didn't supply a reason —
  // that's the most informational / least-action-required hint, so it
  // can't accidentally nag admins into reconnecting Gmail or inviting
  // a user that's already there. Older API responses (or test mocks
  // built before task #227) land on this branch.
  const senderMissingReason: SenderMissingReason =
    status?.senderMissingReason ?? "legacy-row";
  // Flatten every loaded page into one list — the "Load older" button
  // appends a new page server-side, useInfiniteQuery accumulates the
  // pages here and we render them as a single chronological list
  // (task #220).
  const historyEntries =
    historyQuery.data?.pages.flatMap((p) => p.entries) ?? [];
  const hasOlderPage = historyQuery.hasNextPage ?? false;
  const isFetchingOlderPage = historyQuery.isFetchingNextPage;

  return (
    <Card className={className} data-testid="card-seed-notification-history">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="w-4 h-4" />
          Seed-failure notification history
        </CardTitle>
        <CardDescription>
          Confirms whether (and when) the boot-time seeder paged admins about
          failed preset templates. Use the test-send button to verify
          deliverability without waiting for a real regression.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {statusQuery.isLoading ? (
          <div
            className="flex items-center gap-2 text-sm text-muted-foreground"
            data-testid="text-seed-notification-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading notification history…
          </div>
        ) : lastNotifiedAt ? (
          <div className="space-y-2 text-sm">
            <div data-testid="text-seed-notification-last-sent">
              <span className="text-muted-foreground">Last notice sent:</span>{" "}
              <span
                className="font-medium"
                title={lastNotifiedAt.toLocaleString()}
              >
                {formatDistanceToNow(lastNotifiedAt, { addSuffix: true })}
              </span>
              <span className="text-muted-foreground ml-1">
                ({lastNotifiedAt.toLocaleString()})
              </span>
              {senderEmail ? (
                <>
                  {" · "}
                  <span
                    className="text-muted-foreground"
                    data-testid="text-seed-notification-sender"
                  >
                    Sent as{" "}
                    <span className="font-mono">{senderEmail}</span>
                  </span>
                </>
              ) : (
                <>
                  {" · "}
                  <SenderMissingHint reason={senderMissingReason} />
                </>
              )}
            </div>
            <div>
              <div className="text-muted-foreground mb-1">
                Notified seed keys:
              </div>
              {notifiedSeedKeys.length === 0 ? (
                <span
                  className="text-xs italic text-muted-foreground"
                  data-testid="text-seed-notification-empty-keys"
                >
                  (none recorded)
                </span>
              ) : (
                <ul
                  className="list-disc pl-5 text-xs space-y-1"
                  data-testid="list-seed-notification-keys"
                >
                  {notifiedSeedKeys.map((k) => (
                    <li
                      key={k}
                      className="font-mono"
                      data-testid={`text-seed-notification-key-${k}`}
                    >
                      {k}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <div
            className="flex items-center gap-2 text-sm text-muted-foreground"
            data-testid="text-seed-notification-none"
          >
            <Mail className="w-4 h-4" />
            No seed-failure notice has been sent for this workspace.
          </div>
        )}

        {/*
         * Per-fan-out audit log (task #200). Renders the chronological
         * list of recent sends so admins can see which Gmail account
         * each individual notice was sent from, not just the most
         * recent dedup row.
         */}
        <div className="space-y-2">
          <div className="text-sm font-medium" data-testid="text-seed-notification-history-heading">
            Recent sends
          </div>
          {historyQuery.isLoading ? (
            <div
              className="flex items-center gap-2 text-xs text-muted-foreground"
              data-testid="text-seed-notification-history-loading"
            >
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading…
            </div>
          ) : historyEntries.length === 0 ? (
            <div
              className="text-xs italic text-muted-foreground"
              data-testid="text-seed-notification-history-empty"
            >
              No sends recorded yet.
            </div>
          ) : (
            <ul
              className="space-y-2 text-xs"
              data-testid="list-seed-notification-history"
            >
              {historyEntries.map((entry) => {
                const sentAt = new Date(entry.sentAt);
                return (
                  <li
                    key={entry.id}
                    className="border rounded-md px-3 py-2 space-y-1"
                    data-testid={`row-seed-notification-history-${entry.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span
                        className="font-medium"
                        title={sentAt.toLocaleString()}
                        data-testid={`text-seed-notification-history-sent-${entry.id}`}
                      >
                        {formatDistanceToNow(sentAt, { addSuffix: true })}
                      </span>
                      <span className="text-muted-foreground">
                        {sentAt.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      {entry.senderEmail ? (
                        <span data-testid={`text-seed-notification-history-sender-${entry.id}`}>
                          Sent as{" "}
                          <span className="font-mono">{entry.senderEmail}</span>
                        </span>
                      ) : (
                        <span
                          className="italic"
                          data-testid={`text-seed-notification-history-sender-unknown-${entry.id}`}
                        >
                          Sender not recorded
                        </span>
                      )}
                      {" · "}
                      <span
                        data-testid={`text-seed-notification-history-recipients-${entry.id}`}
                      >
                        {entry.recipientCount} recipient
                        {entry.recipientCount === 1 ? "" : "s"}
                      </span>
                    </div>
                    {entry.notifiedSeedKeys.length > 0 ? (
                      <div
                        className="font-mono text-muted-foreground break-all"
                        data-testid={`text-seed-notification-history-keys-${entry.id}`}
                      >
                        {entry.notifiedSeedKeys.join(", ")}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
          {/*
           * Cursor-based "Load older" affordance (task #220). Only
           * rendered when the server signaled there's more history to
           * walk (`nextCursor` was non-null on the last page) — at the
           * tail of the log this stays hidden so admins aren't tempted
           * to keep clicking into emptiness.
           */}
          {hasOlderPage ? (
            <div className="pt-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => historyQuery.fetchNextPage()}
                disabled={isFetchingOlderPage}
                data-testid="button-load-older-seed-notifications"
              >
                {isFetchingOlderPage ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ChevronDown className="w-4 h-4 mr-2" />
                )}
                Load older
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-3 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => testSendMutation.mutate()}
            disabled={testSendMutation.isPending}
            data-testid="button-send-seed-test-notice"
          >
            {testSendMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send me a fresh test notice
          </Button>
          <span className="text-xs text-muted-foreground">
            Clears the dedup row first, then re-runs the notifier.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
